import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Dark mode color scheme - Professional palette with improved contrast
export const darkTheme = {
  background: '#0F1419',           // Very dark gray, not pure black
  cardBackground: '#1A1F29',       // Slightly lighter for cards
  inputBackground: '#252C38',      // For inputs, sections
  text: '#E8EAED',                 // Off-white, easier on eyes
  textSecondary: '#9AA0A6',        // Medium gray for labels
  textTertiary: '#5F6368',         // Darker gray for hints
  sectionHeader: '#1A1F29',
  accent: '#FF6B6B',               // Softer coral red for dark bg
  accentBlue: '#64B5F6',           // Lighter blue for better contrast
  income: '#81C784',               // Lighter green for income
  expense: '#FF6B6B',              // Same as accent
  success: '#81C784',              // Lighter green
  divider: '#3C4043',              // Subtle borders
  tabBar: '#1A1F29',
  hover: '#252C38',                // Hover state
  shadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
};

// Light mode color scheme
export const lightTheme = {
  background: '#F8F9FA',
  cardBackground: '#FFFFFF',
  inputBackground: '#FFFFFF',
  text: '#212529',
  textSecondary: '#6C757D',
  textTertiary: '#ADB5BD',
  sectionHeader: '#E9ECEF',
  accent: '#E8544F',
  accentBlue: '#5B9FE3',
  income: '#4CAF50',
  expense: '#E8544F',
  success: '#4CAF50',
  divider: '#DEE2E6',
  tabBar: '#FFFFFF',
  hover: '#F1F3F4',
  shadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
};

const THEME_STORAGE_KEY = 'spend-easy-theme';

export const ThemeProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [themeMode, setThemeMode] = useState(() => {
    // Initialize from localStorage for instant apply
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored || 'system';
  });

  // Listen for system theme changes
  const [systemTheme, setSystemTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    if (themeMode !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  const actualTheme = themeMode === 'system' ? systemTheme : themeMode;

  // Apply theme to document
  useEffect(() => {
    const currentTheme = actualTheme === 'dark' ? darkTheme : lightTheme;
    
    // Apply CSS variables
    document.documentElement.style.setProperty('--bg-primary', currentTheme.background);
    document.documentElement.style.setProperty('--bg-card', currentTheme.cardBackground);
    document.documentElement.style.setProperty('--bg-input', currentTheme.inputBackground);
    document.documentElement.style.setProperty('--text-primary', currentTheme.text);
    document.documentElement.style.setProperty('--text-secondary', currentTheme.textSecondary);
    document.documentElement.style.setProperty('--text-tertiary', currentTheme.textTertiary);
    document.documentElement.style.setProperty('--app-accent', currentTheme.accent);
    document.documentElement.style.setProperty('--accent', actualTheme === 'dark' ? '0 100% 71%' : '0 0% 96.1%');
    document.documentElement.style.setProperty('--accent-blue', currentTheme.accentBlue);
    document.documentElement.style.setProperty('--color-income', currentTheme.income);
    document.documentElement.style.setProperty('--color-expense', currentTheme.expense);
    document.documentElement.style.setProperty('--color-success', currentTheme.success);
    document.documentElement.style.setProperty('--divider', currentTheme.divider);
    document.documentElement.style.setProperty('--bg-tabbar', currentTheme.tabBar);
    document.documentElement.style.setProperty('--bg-hover', currentTheme.hover);

    // Add/remove dark class on body
    if (actualTheme === 'dark') {
      document.body.classList.add('dark-theme');
      document.documentElement.classList.add('dark');
    } else {
      document.body.classList.remove('dark-theme');
      document.documentElement.classList.remove('dark');
    }
  }, [actualTheme]);

  // Sync with Firebase when user is logged in
  useEffect(() => {
    const loadThemeFromFirebase = async () => {
      if (!currentUser) return;
      
      try {
        const userDocRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
        const docSnap = await getDoc(userDocRef);
        
        if (docSnap.exists() && docSnap.data().themeMode) {
          const firebaseTheme = docSnap.data().themeMode;
          setThemeMode(firebaseTheme);
          localStorage.setItem(THEME_STORAGE_KEY, firebaseTheme);
        }
      } catch (error) {
        console.error('Error loading theme from Firebase:', error);
      }
    };

    loadThemeFromFirebase();
  }, [currentUser]);

  // Save theme preference
  const setTheme = async (mode) => {
    setThemeMode(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);

    // Save to Firebase if logged in
    if (currentUser) {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
        await setDoc(userDocRef, { themeMode: mode }, { merge: true });
      } catch (error) {
        console.error('Error saving theme to Firebase:', error);
      }
    }
  };

  const theme = actualTheme === 'dark' ? darkTheme : lightTheme;
  const isDark = actualTheme === 'dark';

  const value = {
    themeMode,
    setTheme,
    theme,
    isDark,
    resolvedTheme: actualTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
